import { Fragment } from "react";
import { Transition } from "@headlessui/react";
import { tv } from "tailwind-variants";

import { focusRing, icons } from "@/ui";
import toastIcons from "./toast-icons";
import type { Toast } from "./toast-store";

const toastVariants = tv({
  slots: {
    background: "p-4",
    title: "leading-5 font-semibold text-content",
    message: "text-sm",
    icon: `inline-flex size-9 cursor-pointer items-center justify-center rounded-full transition-colors hover:bg-surface-hover ${focusRing}`,
  },
  variants: {
    type: {
      success: {
        background: "bg-success-surface",
        title: "text-success",
        message: "text-success",
        icon: "text-success",
      },
      info: {
        background: "",
        title: "text-content-muted",
        message: "text-content-muted",
        icon: "",
      },
      error: {
        background: "",
        title: "text-content-muted",
        message: "text-content-muted",
        icon: "",
      },
      warning: {
        background: "",
        title: "text-content-muted",
        message: "text-content-muted",
        icon: "",
      },
    },
  },
});

const { background, icon, message, title } = toastVariants();

export type ToastMessageProps = {
  toast: Toast;
  onClose: () => void;
};

export const ToastMessage = ({ onClose, toast }: ToastMessageProps) => {
  return (
    <Transition
      as={Fragment}
      enter="transform ease-out duration-300 transition"
      enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
      enterTo="translate-y-0 opacity-100 sm:translate-x-0"
      leave="transition-all ease-in duration-500"
      leaveFrom="opacity-100 max-h-52"
      leaveTo="opacity-0 not-last:max-h-0"
      show={toast.state === "open"}
      appear
    >
      <div
        className="pointer-events-auto z-50 w-full max-w-sm overflow-hidden rounded-card border border-line bg-surface shadow-overlay"
        onClick={onClose}
      >
        <div className={background({ type: toast.type })}>
          <div className="flex items-start">
            <div className="mt-0.5 shrink-0">
              {toast.icon ? toast.icon : toastIcons[toast.type]}
            </div>

            <div className="ml-4 flex w-0 flex-1 flex-col gap-1 pr-2">
              {toast.title ? <p className={title({ type: toast.type })}>{toast.title}</p> : null}

              {toast.message ? (
                <p className={message({ type: toast.type })}>{toast.message}</p>
              ) : null}
            </div>

            <div className="ml-2 flex shrink-0">
              <button className={icon({ type: toast.type })} onClick={onClose} type="button">
                <span className="sr-only">Close</span>
                <icons.XMarkIcon aria-hidden="true" className="size-4" strokeWidth={2} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  );
};
